
































































































































































































































import { Component, Prop } from 'vue-property-decorator';
import Avatar from '@/components/common/Avatar.vue';
import { LockIcon } from 'vue-feather-icons';
import dayjs from 'dayjs';
import { ITag, ITask, TaskPriority, TaskStatus } from 'admiral-core';
import TaskStatusIcon from '@/components/tasks/TaskStatusIcon.vue';
import TaskPriorityIcon from '@/components/tasks/TaskPriorityIcon.vue';
import ProductBadge from '@/components/common/ProductBadge.vue';
import { mixins } from 'vue-class-component';
import { BaseMixin } from '@/mixins/BaseMixin';
import { FormatMixin } from '@/mixins/FormatMixin';
import TaskStatusMenu from '@/components/tasks/TaskStatusMenu.vue';
import TaskPriorityMenu from '@/components/tasks/TaskPriorityMenu.vue';
import TaskDueDatePopover from '@/components/tasks/TaskDueDatePopover.vue';
import PolicyProductShort from '@/components/common/PolicyProductShort.vue';
import PolicyCustomerShort from '@/components/common/PolicyCustomerShort.vue';
import DamageShort from '@/components/common/DamageShort.vue';
import { TaskApi } from '@/services/TaskApi';

@Component({
  name: 'TaskListViewItem',
  components: {
    ProductBadge,
    Avatar,
    LockIcon,
    TaskStatusMenu,
    TaskPriorityMenu,
    TaskStatusIcon,
    TaskPriorityIcon,
    TaskDueDatePopover,
    PolicyProductShort,
    PolicyCustomerShort,
    DamageShort,
  },
})
export default class TaskListViewItem extends mixins(BaseMixin, FormatMixin) {
  @Prop({ type: Object, required: true }) task!: ITask;

  /** Policy/damage embed: priority + status + title only (no drag, links, tags, due date, avatars). */
  @Prop({ type: Boolean, default: false }) minimal!: boolean;

  /**
   * tasks2 list: one shared overlay for status/priority (no per-row b-dropdown).
   * Other list embeds keep per-row TaskStatusMenu / TaskPriorityMenu.
   */
  @Prop({ type: Boolean, default: false }) singletonMenus!: boolean;

  detailed = true;
  hoveredPolicy = '';
  hoveredDamage = '';

  get taskTags() {
    return this.task.tags || [];
  }

  /** Supports full `files` relation or optional `filesCount` from lean payloads. */
  get filesIndicatorCount(): number {
    const n = this.task.filesCount;
    if (typeof n === 'number') return n;
    return this.task.files?.length ?? 0;
  }

  /** Supports full `comments` relation or optional `commentsCount` from lean payloads. */
  get commentsIndicatorCount(): number {
    const n = this.task.commentsCount;
    if (typeof n === 'number') return n;
    return this.task.comments?.length ?? 0;
  }

  get tagsIndicatorCount(): number {
    const n = this.task.tagsCount;
    if (typeof n === 'number') return n;
    return this.task.tags?.length ?? 0;
  }

  get taskPriority(): TaskPriority {
    return this.task.priority ?? TaskPriority.NO_PRIORITY;
  }

  openSingletonPriority(e: MouseEvent) {
    const anchor = e.currentTarget as HTMLElement;
    this.$emit('open-list-singleton-menu', {
      kind: 'priority',
      task: this.task,
      anchor,
    });
  }

  openSingletonStatus(e: MouseEvent) {
    const anchor = e.currentTarget as HTMLElement;
    this.$emit('open-list-singleton-menu', {
      kind: 'status',
      task: this.task,
      anchor,
    });
  }

  tagClass(tagIndex: number) {
    return this.tagClasses[tagIndex % this.tagClasses.length];
  }

  formatDate(date: Date | string | undefined) {
    if (!date) return '';
    const d = dayjs(date);
    return d.year() === dayjs().year() ? d.format('DD.MM') : d.format('DD.MM.YYYY');
  }

  formatDueDate(date: Date | string | undefined) {
    if (!date) return '';
    const due = dayjs(date);
    if (due.isSame(dayjs(), 'day')) return 'Heute';
    return due.year() === dayjs().year() ? due.format('DD.MM') : due.format('DD.MM.YYYY');
  }

  get isOverdue() {
    if (!this.task.dueAt) return false;
    const activeStatuses = ['TODO', 'IN_PROGRESS', 'IN_WAITING'];
    if (!activeStatuses.includes(this.task.status)) return false;
    return dayjs(this.task.dueAt).isBefore(dayjs(), 'day');
  }

  get dueDateClass(): Record<string, boolean> {
    if (!this.task.dueAt) return {};
    const due = dayjs(this.task.dueAt);
    const today = dayjs().startOf('day');
    const weekEnd = dayjs().endOf('week');
    if (due.isBefore(today, 'day')) return { 'text-danger': true };
    if (due.isSame(today, 'day')) return { 'text-dark font-weight-bold': true };
    if (!due.isAfter(weekEnd)) return { 'text-dark': true };
    return {};
  }

  tagClassById(tagId: string) {
    return this.tagClasses[
      this.task.tags.findIndex((tag: ITag) => tag.id === tagId) % this.tagClasses.length
    ];
  }

  getPolicyTitle(policy: any): string {
    const status = this.formatStatusLabel(policy);
    const product = policy?.product?.name || 'Kein Produkt';
    const customer = policy?.customer || '';
    return `${status}: ${product} für ${customer}`;
  }

  firstCharacter(tagName: string) {
    const tagNameSplit = tagName.split(' ');
    let result = '';
    tagNameSplit.forEach((word: string) => {
      result += word.substr(0, 1).toUpperCase();
    });
    return result;
  }

  async onStatusChange(newStatus: TaskStatus) {
    try {
      await TaskApi.updateStatus(this.task.id, this.task, newStatus);
      this.$set(this.task, 'status', newStatus);
      this.$emit('status-changed');
    } catch (e) {
      this.$toastr.e('Status konnte nicht geändert werden.');
    }
  }

  async onPriorityChange(newPriority: TaskPriority) {
    try {
      await TaskApi.updatePriority(this.task.id, this.task, newPriority);
      this.$set(this.task, 'priority', newPriority);
      this.$emit('priority-changed');
    } catch (e) {
      this.$toastr.e('Priorität konnte nicht geändert werden.');
    }
  }
}
